import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import {
  type Magento2_CartItemInput,
  Magento2_ProductStockStatus,
} from '@__generated__/globalTypes';
import { Button, getButtonStyles } from '@app/components/Button';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { isNotNullish } from '@app/helpers/predicate';
import useAddProductsToCart from '@app/hooks/useAddProductsToCart';
import { routes } from '@app/routes/shop';
import type { AppTheme } from '@app/styles/theme';

import { constructJPStoreLink, isJPHardware } from '../../helpers';
import type {
  PricingPageShopProduct_magento2_products_items_Magento2_BundleProduct as BundleProduct,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants_product as VariantProduct,
  PricingPageShopProduct_magento2_products_items_Magento2_VirtualProduct as VirtualProduct,
} from '../../hook/__generated__/PricingPageShopProduct';
import messages from './AddToCartButton.messages';

const CART_ROUTE_NAME = 'cart';

export interface AddToCartButtonProps {
  product?: BundleProduct | VirtualProduct | VariantProduct | null;
  quantity?: number;
}

const AddToCartButton = ({ product, quantity = 1 }: AddToCartButtonProps) => {
  const { loading: cartIdLoading } = useGetCartId();
  const { addProductsResult, addItems } = useAddProductsToCart();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { language } = useParams<{
    language: string;
  }>();

  useEffect(() => {
    const result = addProductsResult?.data?.magento2?.addProductsToCart;
    const cartRoute = routes.find(
      (route) => route.name === CART_ROUTE_NAME,
    )?.path;

    if (!addProductsResult?.loading) {
      setLoading(false);
    }

    if (result && cartRoute && !result.userErrors?.length) {
      const cartUrl = generatePath(cartRoute, {
        language,
      });
      history.push(cartUrl);
    }
  }, [addProductsResult, history, language]);

  /* In some cases the 'Ni Hidden' category is not updated immediately after enabling
   * a product on Magento. With this double check we make sure they are not added to cart
   */
  const isDisabled =
    cartIdLoading ||
    product?.stockStatus === Magento2_ProductStockStatus.OUT_OF_STOCK ||
    // TODO [WWW-11705] Migrate NI to new graphql-codegen generator syntax.
    // @ts-expect-error For now we just expect this error.
    product?.categories?.find((category) => category.name === 'NI Hidden');

  const addToCart = () => {
    let uids: string[] = [];
    if (product?.__typename === 'Magento2_BundleProduct') {
      uids = (
        product?.items
          ?.map((item) => item?.options?.map((option) => option?.uid)[0])
          .filter(isNotNullish) as string[]
      ).filter(isNotNullish) as string[];
    }

    const cartItem: Magento2_CartItemInput = {
      sku: product?.sku as string,
      quantity,
      selected_options: uids,
    };

    setLoading(true);
    addItems([cartItem]);
  };

  return (
    <>
      {isJPHardware(product) ? (
        <a
          href={constructJPStoreLink(product?.sku)}
          css={(theme: AppTheme) => getButtonStyles({ type: 'primary', theme })}
          data-testid={`media-integration-button-${product?.sku}`}
        >
          <FormattedMessage {...messages.buyAtMediaIntegration} />
        </a>
      ) : (
        <Button
          data-testid={`add-to-cart-button-${product?.sku}`}
          isLoading={loading}
          onClick={loading || isDisabled ? undefined : addToCart}
          type="primary"
          disabled={isDisabled}
        >
          <FormattedMessage {...messages.addToCart} />
        </Button>
      )}
    </>
  );
};

export default AddToCartButton;
